// src/features/tidsregistering/components/day-overview/DayTimeline.jsx
//
// Two-track horizontal day visualization:
//   Top track  (planned)  — solid band per planned shift, grey when empty
//   Bottom track (actual) — colored blocks per time detail
//
// Props:
//   timeDetails        — TimeDetail[]           actual registered blocks
//   registrationCodes  — RegistrationCode[]     for color + label lookup
//   plannedShifts      — PlannedShift[]         planned shift(s) for the day (top track)
//   windowStartHour    — number  default 6      left edge of both strips
//   windowEndHour      — number  default 20     right edge of both strips
//   height             — number  default 20     height of the actual (bottom) track in px
//   showAxis           — boolean default false  hour labels below strip

import React from 'react';
import { Box, Typography } from '@mui/material';
import { CATEGORY_COLORS, CODE_COLORS, REGISTRATION_CATEGORY_COLORS, DEFAULT_COLOR, SHIFT_COLOR } from '../../constants/colors';
import LightTooltip from '../../../../components/common/LightTooltip';

function timeStrToMinutes(t) {
  if (!t) return null;
  const parts = String(t).split(':').map(Number);
  return parts[0] * 60 + (Number.isFinite(parts[1]) ? parts[1] : 0);
}

function toPct(minutes, windowStart, windowDuration) {
  return Math.max(0, Math.min(100, ((minutes - windowStart) / windowDuration) * 100));
}

export function resolveColor(rc) {
  if (!rc) return DEFAULT_COLOR;
  return REGISTRATION_CATEGORY_COLORS[rc.registration_category]
    ?? CATEGORY_COLORS[rc.statistical_category]
    ?? CODE_COLORS[rc.work_time_type]
    ?? DEFAULT_COLOR;
}

function Track({ height, bgcolor = 'grey.100', children }) {
  return (
    <Box sx={{ position: 'relative', height, bgcolor, borderRadius: 0.5, overflow: 'hidden' }}>
      {children}
    </Box>
  );
}

function ShiftTip({ shift }) {
  return (
    <Box sx={{ p: 1, minWidth: 120 }}>
      <Typography variant="caption" fontWeight={600} display="block">
        {String(shift.start_time).slice(0, 5)} – {String(shift.end_time).slice(0, 5)}
      </Typography>
    </Box>
  );
}

function BlockTip({ td, label }) {
  return (
    <Box sx={{ p: 1, minWidth: 130 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.25 }}>{label}</Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        {String(td.start_time).slice(0, 5)} – {String(td.end_time).slice(0, 5)}
      </Typography>
      <Typography variant="caption" fontWeight={600}>
        {Number(td.hours_sum).toFixed(1)} t
      </Typography>
    </Box>
  );
}

export default function DayTimeline({
  timeDetails = [],
  registrationCodes = [],
  plannedShifts = [],
  windowStartHour = 6,
  windowEndHour = 20,
  height = 20,
  showAxis = false,
}) {
  const codeMap = new Map(registrationCodes.map((rc) => [rc.registration_code_id, rc]));
  const windowStart = windowStartHour * 60;
  const windowEnd = windowEndHour * 60;
  const windowDuration = windowEnd - windowStart;

  const latest = timeDetails.filter((td) => td.is_latest_version !== false);

  const tickHours = [];
  for (let h = windowStartHour + 1; h < windowEndHour; h++) tickHours.push(h);
  const axisHours = [windowStartHour, ...tickHours, windowEndHour];

  const ticks = tickHours.map((h) => (
    <Box
      key={h}
      sx={{
        position: 'absolute',
        top: 0, bottom: 0,
        left: `${toPct(h * 60, windowStart, windowDuration)}%`,
        width: '1px',
        bgcolor: 'grey.300',
        pointerEvents: 'none',
      }}
    />
  ));

  const PLANNED_HEIGHT = 10;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>

      {/* Planned shift track */}
      <Track height={PLANNED_HEIGHT}>
        {ticks}
        {plannedShifts.length === 0 ? null : plannedShifts.map((shift, i) => {
          const startMin = timeStrToMinutes(shift.start_time);
          const endMin = timeStrToMinutes(shift.end_time);
          if (startMin === null || endMin === null) return null;
          const left = toPct(startMin, windowStart, windowDuration);
          const width = toPct(endMin, windowStart, windowDuration) - left;
          if (width <= 0) return null;
          return (
            <LightTooltip key={shift.planned_shift_id ?? i} title={<ShiftTip shift={shift} />} arrow enterDelay={150}>
              <Box
                sx={{
                  position: 'absolute',
                  top: 2, bottom: 2,
                  left: `${left}%`,
                  width: `${Math.max(width, 0.4)}%`,
                  bgcolor: SHIFT_COLOR,
                  borderRadius: 0.25,
                  opacity: 0.75,
                  cursor: 'default',
                  '&:hover': { opacity: 1, zIndex: 2 },
                }}
              />
            </LightTooltip>
          );
        })}
      </Track>

      {/* Actual time detail track */}
      <Track height={height}>
        {ticks}
        {latest.map((td) => {
          const startMin = timeStrToMinutes(td.start_time);
          const endMin = timeStrToMinutes(td.end_time);
          if (startMin === null || endMin === null) return null;
          const left = toPct(startMin, windowStart, windowDuration);
          const width = toPct(endMin, windowStart, windowDuration) - left;
          if (width < 0) return null;
          const rc = codeMap.get(td.registration_code_id) ?? null;
          const color = resolveColor(rc);
          const label = rc?.title ?? '?';
          return (
            <LightTooltip key={td.time_detail_id} title={<BlockTip td={td} label={label} />} arrow enterDelay={150}>
              <Box
                sx={{
                  position: 'absolute',
                  top: 2, bottom: 2,
                  left: `${left}%`,
                  width: `${Math.max(width, 0.4)}%`,
                  bgcolor: color,
                  borderRadius: 0.25,
                  opacity: 0.85,
                  cursor: 'default',
                  '&:hover': { opacity: 1, zIndex: 2 },
                }}
              />
            </LightTooltip>
          );
        })}
      </Track>

      {/* Optional hour axis */}
      {showAxis && (
        <Box sx={{ position: 'relative', height: 14, mt: 0.25 }}>
          {axisHours.map((h) => (
            <Typography
              key={h}
              variant="caption"
              sx={{
                position: 'absolute',
                left: `${toPct(h * 60, windowStart, windowDuration)}%`,
                transform: 'translateX(-50%)',
                fontSize: 10,
                color: 'text.disabled',
                lineHeight: 1,
                userSelect: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              {String(h).padStart(2, '0')}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
}
